import { useState, type FC } from 'react';
import useUser from 'utils/useUser';
import ManualInteractionTimeModalNew from 'components/molecules/interaction-time-modal';
import { ModuleType } from 'infrastructure/enums';
import { useCheckPatientModule } from 'infrastructure/hooks/patient/use-check-patient-module';
import { useRpmPatientDetails } from 'infrastructure/hooks/rpm';

import {
  useGetCcmInteractionTime,
  useGetRpmInteractionTime,
  useSetManualInteractionTime,
} from './hooks';
import CombinedInteractiveTime from './components/combined';
import CombinedInteractiveTimeMobile from './components/combined-mobile';

interface Props {
  patientGuid: string;
}

const RpmCcmInteractiveTime: FC<Props> = ({ patientGuid }) => {
  const { clinicReviewTimeSeconds } = useRpmPatientDetails({ patientGuid });
  const [manualInteractionsPopupOpen, setManualInteractionsPopupOpen] =
    useState(false);
  const { isMobileDevice } = useUser();
  const { isBlockCcmModule, isBlockRpmModule } =
    useCheckPatientModule(patientGuid);

  const { ccmInteractionTime, isFetching: isFetchCcm } =
    useGetCcmInteractionTime({
      patientGuid,
      enabled: true,
    });
  const { rpmInteractionTime, isFetching: isFetchRpm } =
    useGetRpmInteractionTime({
      patientGuid,
      enabled: true,
    });
  const { setManualInteractionTime, isLoading } = useSetManualInteractionTime({
    onSuccess() {
      setManualInteractionsPopupOpen(false);
    },
  });

  const today = new Date();

  return (
    <>
      {isMobileDevice ? (
        <CombinedInteractiveTimeMobile
          ccmData={ccmInteractionTime}
          rpmData={rpmInteractionTime}
          loading={isFetchCcm || isFetchRpm}
          onAdd={() => setManualInteractionsPopupOpen(true)}
          addLoading={isLoading}
          today={today}
        />
      ) : (
        <CombinedInteractiveTime
          clinicReviewTimeSeconds={clinicReviewTimeSeconds}
          ccmData={ccmInteractionTime}
          rpmData={rpmInteractionTime}
          loading={isFetchCcm || isFetchRpm}
          onAdd={() => setManualInteractionsPopupOpen(true)}
          addLoading={isLoading}
          today={today}
        />
      )}
      <ManualInteractionTimeModalNew
        isOpen={manualInteractionsPopupOpen}
        toggle={() => setManualInteractionsPopupOpen(false)}
        patientGuid={patientGuid}
        module={ModuleType.RPMCCM}
        loading={isLoading}
        submit={setManualInteractionTime}
        disableCcm={isBlockCcmModule}
        disableRpm={isBlockRpmModule}
      />
    </>
  );
};

export default RpmCcmInteractiveTime;
